import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  InputGroup,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { SelectField } from '@/components/SelectField';
import { ModalFormData, modalFormSchema } from './schemas';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ImageUpload } from '@/components/ImageUpload';
import { UniformGallery, UniformSeason } from '@/model';
import { PutMediaUniformGalleryRequest } from '@/services/request/uniform-gallery';
import { usePutUniformGalleryMutation } from '@/services/queries/uniform-gallery';
import { useTranslation } from 'react-i18next';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';

export interface UniformGalleryEditModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  item: UniformGallery;
  seasons: UniformSeason[] | undefined;
  onClose: () => void;
  onDone?: () => void;
}

export const UniformGalleryEditModal = ({ isOpen, seasons, onClose, item, onDone }: UniformGalleryEditModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const putItemMutation = usePutUniformGalleryMutation();

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    formState: { errors },
    clearErrors,
    control,
  } = useForm<ModalFormData>({
    defaultValues: {
      title: item.title,
      season_uuid: item.season_uuid,
      image_url: item.image_url,
      image: undefined,
    },
    resolver: zodResolver(modalFormSchema),
    criteriaMode: 'all',
  });
  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      if (!item) {
        return;
      }

      const payload: PutMediaUniformGalleryRequest = {
        ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
        image: data.image,
        title: data.title,
        credit: data.credit,
        season_uuid: data.season_uuid,
      };

      putItemMutation.mutate(
        {
          id: item.uuid,
          payload,
        },
        {
          onSuccess: () => {
            onDone?.();
            toast({
              status: 'success',
              title: t('pages.application_uniform_gallery.components.uniform_gallery_edit_modal.uniform_gallery_edit_success'),
            });
          },
          onError: (resp: any) => {
            toast({
              status: 'error',
              title:
                (resp && resp.message) ||
                t('pages.application_uniform_gallery.components.uniform_gallery_edit_modal.uniform_gallery_edit_error'),
            });
          },
        },
      );
    },
    [item, onDone, putItemMutation, toast, t],
  );

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_uniform_gallery.components.uniform_gallery_edit_modal.uniform_gallery_edit_title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              prefetchOnOpen
              label={t('pages.application_uniform_gallery.components.uniform_gallery_edit_modal.uniform_gallery_edit_team_label')}
              placeholder={t(
                'pages.application_uniform_gallery.components.uniform_gallery_edit_modal.uniform_gallery_edit_team_label',
              )}
              showFlag
              width='100%'
              usePortal={false}
              error={errors?.team_uuid?.message as string | undefined}
            />

            <InputGroup display='flex' flexDirection='column'>
              <Text fontWeight='medium'>
                {t('pages.application_uniform_gallery.components.uniform_gallery_edit_modal.uniform_gallery_edit_season_label')}
              </Text>
              <SelectField
                defaultValue=''
                {...register('season_uuid')}
                rootProps={{ isInvalid: !!errors?.season_uuid, errorMessage: errors?.season_uuid?.message, mt: 0 }}
              >
                <option value='' disabled>
                  {t(
                    'pages.application_uniform_gallery.components.uniform_gallery_edit_modal.uniform_gallery_edit_season_default_option',
                  )}
                </option>

                {seasons?.map(item => (
                  <option key={item.uuid} value={item.uuid}>
                    {item.title}
                  </option>
                ))}
              </SelectField>
            </InputGroup>

            <InputField
              withoutFloating
              label={t(
                'pages.application_uniform_gallery.components.uniform_gallery_edit_modal.uniform_gallery_edit_title_label',
              )}
              helperText={t(
                'pages.application_uniform_gallery.components.uniform_gallery_edit_modal.uniform_gallery_edit_title_helper_text',
              )}
              maxLength={100}
              rootProps={{ isInvalid: !!errors?.title, errorMessage: errors?.title?.message }}
              {...register('title')}
            />

            <InputField
              withoutFloating
              label={t(
                'pages.application_uniform_gallery.components.uniform_gallery_edit_modal.uniform_gallery_edit_credit_label',
              )}
              {...register('credit')}
              maxLength={50}
              rootProps={{ isInvalid: !!errors?.credit, errorMessage: errors?.credit?.message }}
            />

            <InputGroup display='flex' flexDirection='column' width='100%'>
              <ImageUpload
                label={t(
                  'pages.application_uniform_gallery.components.uniform_gallery_edit_modal.uniform_gallery_edit_image_label',
                )}
                imageURL={item.image_url ?? ''}
                onFileChange={file => {
                  setValue('image', file);
                }}
                helpText={t(
                  'pages.application_uniform_gallery.components.uniform_gallery_edit_modal.uniform_gallery_edit_image_helper_text',
                )}
                error={errors?.image?.message?.toString()}
              />
            </InputGroup>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent='flex-end' gap='kilo'>
          <Flex gap={2}>
            <Button variant='outlined' color='gray.600' onClick={onClose}>
              {t('pages.application_uniform_gallery.components.uniform_gallery_edit_modal.uniform_gallery_edit_cancel_button')}
            </Button>
            <Button onClick={handleConfirm}>
              {t('pages.application_uniform_gallery.components.uniform_gallery_edit_modal.uniform_gallery_edit_submit_button')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>

      <CenteredSpinner isLoading={putItemMutation.isLoading} />
    </Modal>
  );
};
